/**
 * AppShell — fixed desktop layout with permanent sidebar
 *
 * Desktop: sidebar always visible at SIDEBAR_W, content starts immediately after.
 *   No pinned/unpinned states. No hover expand. No overlay. No animation.
 *   The sidebar is structural, not interactive.
 *
 * Mobile (<lg): sidebar hidden behind a Sheet drawer, triggered by hamburger.
 */

import React, { useState, lazy, Suspense, useCallback } from 'react'
import Sidebar, { SIDEBAR_W, SIDEBAR_W_COLLAPSED } from './Sidebar'
import CommandRow from './CommandRow'
import DevBillingBanner from './DevBillingBanner'
import { ExpiredTrialBanner } from '../billing/ExpiredTrialBanner'
import { Sheet, SheetContent, SheetTrigger } from './sheet'
import { Button } from './button'
import { Menu } from 'lucide-react'
import { useTenant } from '../../contexts/TenantContext'
import { useCreateEmployee, dispatchEmployeeCreated } from '../../contexts/CreateEmployeeContext'
import { useToast } from '../../hooks/useToast'

const CreateEmployee = lazy(() => import('../admin/CreateEmployee'))

interface AppShellProps {
  children: React.ReactNode
  /** Opts the current route out of the centered max-width column — for spatial workspaces (a canvas, a board) that need the full available width instead of a document-width one. Desktop only; the mobile branch already has no max-width. */
  fullWidth?: boolean
}

export default function AppShell({ children, fullWidth = false }: AppShellProps) {
  const [mobileOpen,  setMobileOpen]  = useState(false)
  const [collapsed,   setCollapsed]   = useState(false)
  const sidebarW = collapsed ? SIDEBAR_W_COLLAPSED : SIDEBAR_W
  const { tenant } = useTenant()
  const { open: createOpen, prefill: createPrefill, closeCreateEmployee } = useCreateEmployee()
  const { toast } = useToast()

  const handleCreateSuccess = useCallback(() => {
    dispatchEmployeeCreated()
    toast({ title: 'Onboarding launched', description: 'New hire added and journey seeded.' })
  }, [toast])

  return (
    <div style={{ background: '#F8FAFC', minHeight: '100dvh' }}>

      {/* ── Desktop (lg+) ─────────────────────────────────────────────────── */}
      <div className="hidden lg:block">

        {/* Fixed sidebar */}
        <aside
          className="fixed left-0 top-0"
          style={{ width: sidebarW, height: '100dvh', zIndex: 40, transition: 'width 200ms cubic-bezier(0.4,0,0.2,1)' }}
        >
          <Sidebar
            tenantName={tenant?.name}
            collapsed={collapsed}
            onCollapsedChange={setCollapsed}
          />
        </aside>

        {/* Content: margin-left tracks sidebar width */}
        <div
          style={{
            marginLeft: sidebarW,
            transition: 'margin-left 200ms cubic-bezier(0.4,0,0.2,1)',
            minHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <DevBillingBanner />
          <ExpiredTrialBanner />

          {/* Top command strip — sticky within the content column */}
          <div
            className="sticky z-30"
            style={{
              top: 0,
              padding: '8px 18px 0',
              background: '#F8FAFC',
            }}
          >
            <CommandRow />
          </div>

          <main
            style={
              fullWidth
                ? { flex: 1, minHeight: 0, width: '100%', padding: '10px 20px 20px' }
                : { flex: 1, maxWidth: 1440, width: '100%', margin: '0 auto', padding: '10px 18px 28px' }
            }
          >
            {children}
          </main>
        </div>
      </div>

      {/* ── Mobile (<lg) ──────────────────────────────────────────────────── */}
      <div className="lg:hidden">
        <header
          className="sticky top-0 z-40 flex items-center gap-2 px-3 h-14"
          style={{
            background: 'rgba(248,250,252,0.95)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: '1px solid rgba(0,0,0,0.07)',
          }}
        >
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              {/* The mobile navigation button. An icon with no name, on every
                  screen below the sidebar breakpoint, in the whole admin app —
                  a screen-reader user was offered "button" and nothing else as
                  the only route to navigation. */}
              <Button variant="ghost" size="sm" aria-label="Open the main menu">
                <Menu className="h-5 w-5" aria-hidden />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" style={{ width: SIDEBAR_W + 10, padding: 0 }}>
              <Sidebar
                tenantName={tenant?.name}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>
          <div className="flex-1 min-w-0">
            <CommandRow mobile />
          </div>
        </header>

        <DevBillingBanner />
        <ExpiredTrialBanner />

        <main className="px-4 py-5">
          {children}
        </main>
      </div>

      {/* ── App-level CreateEmployee modal ────────────────────────────────── */}
      {/* Renders above any route — no route transition, no People page needed. */}
      <Suspense fallback={null}>
        <CreateEmployee
          open={createOpen}
          onOpenChange={(open) => { if (!open) closeCreateEmployee() }}
          onSuccess={handleCreateSuccess}
          prefill={createPrefill}
        />
      </Suspense>

    </div>
  )
}
